import { AppUser } from '@eventr/common'
import { IItinerary } from '../state/interfaces'
import { getAllAttendees } from '../v2/utils/activities'
import { getAllPassengers } from '../v2/utils/flights'
import { getAllPeople as getAllHireCarPassengers } from '../v2/utils/hireCars'
import { getAllGuests } from '../v2/utils/hotels'
import { getAllPeople } from '../v2/utils/parking'
import { getAllPeople as getAllTransportPassengers } from '../v2/utils/transport'
import { searchMembersFilter } from './utils/searchMemberFilter'

interface ISearchResponse {
    data: IItinerary
    users: AppUser[]
}
export function search(
    query: string,
    data?: IItinerary,
    users?: AppUser[]
): ISearchResponse | undefined {
    if (!data || !users) {
        return undefined
    }
    // Assuming query will always be a string, so query === undefined is unlikely with TypeScript.
    // If query can be empty, return the original data.
    if (query.length === 0) {
        return { data, users }
    }

    const lowerCaseQuery = query.toLowerCase()

    // Configuration for searchable properties per section
    // Add other sections like 'hotels', 'cars', etc., as needed
    const searchableProperties: Record<number, string[]> = {
        2: [
            'bookref',
            'arrival.arr_name',
            'departure.dep_name',
            'actualBookingRef',
            'inboundAirportAbr',
            'outboundAirportAbr',
        ],
        3: ['accommodationType', 'booking_ref', 'name'],
        8: ['activity_name', 'start', 'end', 'location', 'todo_name'],
        9: ['parking_name'],
        4: ['booking_reference', 'car_alias', 'vehicle_type'],
        5: ['booking_ref', 'transport_alias', 'pick_up'],
    }
    const sectionJsonKeyMap: Record<number, string> = {
        2: 'flights',
        3: 'hotels',
        8: 'activities',
        9: 'parkings',
        4: 'hireCars',
        5: 'transport',
    }
    const filteredUsers = users.filter((user) =>
        searchMembersFilter(user, lowerCaseQuery)
    )
    const userIds = filteredUsers.map((user) => user.id)
    // Helper function to safely access nested property values
    const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((currentObject, key) => {
            return currentObject && currentObject[key] !== undefined
                ? currentObject[key]
                : undefined
        }, obj)
    }

    if (!data.v2 || data.v2.length === 0) {
        return undefined // No v2 items to search
    }

    const filteredV2 = data.v2.map((v2Item) => {
        if (!v2Item.data) {
            return v2Item
        }
        const sectionID = v2Item.sectionID
        const searchProps = searchableProperties[sectionID]
        if (!searchProps) {
            return v2Item
        }

        // Iterate over configured sections (flights, hotels, etc.)
        const itemsToSearch = (v2Item.data as any)[sectionJsonKeyMap[sectionID]] // e.g., v2Item.data.flights
        const propertiesToSearchIn = searchableProperties[sectionID]

        const found = itemsToSearch.filter((item: any) => {
            // Iterate over each item in the section (e.g., each flight)
            const itemUsers = getElementUsers(
                sectionJsonKeyMap[sectionID],
                item
            )
            if (userIds.some((userId) => itemUsers.includes(userId))) {
                return true // User is part of this item, so include it
            }
            // Iterate over the properties specified for searching in this section
            for (const propPath of propertiesToSearchIn) {
                const value = getNestedValue(item, propPath)
                if (
                    typeof value === 'string' &&
                    value.toLowerCase().includes(lowerCaseQuery)
                ) {
                    return true // Found a match, so include this v2Item
                }
            }
        })
        // If any items were found, return the v2Item with the filtered items
        return {
            ...v2Item,
            data: {
                ...v2Item.data,
                [sectionJsonKeyMap[sectionID]]: found,
            },
        }
    })

    // Return a new IItinerary object with the filtered v2 items
    // and preserve other top-level properties of the original itinerary data
    return {
        data: {
            ...data,
            v2: filteredV2,
        },
        users: filteredUsers,
    }
}

const getElementUsers = (sectionKey: string, item: any) => {
    switch (sectionKey) {
        case 'flights':
            return getAllPassengers(item).map((p) => p.option)
        case 'hotels':
            return getAllGuests(item).map((g) => g.option)
        case 'activities':
            return getAllAttendees(item).map((a) => a.option)
        case 'parkings':
            return getAllPeople(item).map((a) => a.option)
        case 'hireCars':
            return getAllHireCarPassengers(item).map((a) => a.option)
        case 'transport':
            return getAllTransportPassengers(item).map((a) => a.option)
        default:
            return []
    }
}
